import connector from './connector'
import { get, patch, post, del } from '@/shared/utils/api-utils'
import { getDocumentsCount } from './documents-api'
import {
  EntityType,
  DisputeStatusFilter,
  TechnicianDocumentType,
} from '@depanneo/types'
import {
  UpdateTechnicianDto,
  TechnicianResponseDto,
  CreateWorkZoneDto,
  UpdateWorkZoneDto,
  WorkZone,
  TechnicianContact,
  CreateContactDto,
  UpdateContactDto,
  CreateAvailabilityDto,
  UpdateAvailabilityDto,
  TechnicianAvailability,
  TechnicianDocument,
} from '@/types/technician'
import type { TechnicianFormData } from '@/shared/types/technicians'
import type {
  ListTechniciansParams,
  PaginatedTechniciansApiResponse,
  TechnicianFilterCounts,
} from '@/shared/types/technicians-table'
import { transformTechnicianData } from '../components/technicians/new/utils/transform-technician-data'
import { objectToFormData } from '@/shared/utils/file-upload-utils'
import { DOCUMENT_KEY_TO_TYPE } from '../components/technicians/new/constants'

export const createTechnician = async (
  data: TechnicianFormData,
  documents?: Record<string, File>
) => {
  // Prepare documents array for FormData
  const documentFiles: File[] = []
  const documentTypes: TechnicianDocumentType[] = []

  if (documents) {
    Object.entries(documents).forEach(([key, file]) => {
      if (file) {
        documentFiles.push(file)
        const docType =
          DOCUMENT_KEY_TO_TYPE[key] || TechnicianDocumentType.COMPLIANCE
        documentTypes.push(docType)
      }
    })
  }

  const formData = objectToFormData(
    {
      ...transformTechnicianData(data),
      documents: documentFiles,
      documentTypes,
    },
    {
      filesField: 'documents',
      jsonFields: [
        'documentTypes',
        'managerContact',
        'operationalContact',
        'secondaryContacts',
        'acceptedPaymentMethods',
        'billingAddress',
        'billingContact',
        'availability',
        'workZones',
      ],
    }
  )

  const response = await connector.post('/technicians', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export async function getTechnicianById(
  id: string
): Promise<TechnicianResponseDto> {
  return await get<TechnicianResponseDto>(`/technicians/${id}`)
}

export async function updateTechnician(
  id: string,
  data: UpdateTechnicianDto
): Promise<TechnicianResponseDto> {
  return await patch<TechnicianResponseDto, UpdateTechnicianDto>(
    `/technicians/${id}`,
    data
  )
}

export async function addWorkZone(
  technicianId: string,
  data: CreateWorkZoneDto
): Promise<WorkZone> {
  return await post<WorkZone, CreateWorkZoneDto>(
    `/technicians/${technicianId}/work-zones`,
    data
  )
}

export async function updateWorkZone(
  technicianId: string,
  zoneId: string,
  data: UpdateWorkZoneDto
): Promise<WorkZone> {
  return await patch<WorkZone, UpdateWorkZoneDto>(
    `/technicians/${technicianId}/work-zones/${zoneId}`,
    data
  )
}

export async function deleteWorkZone(
  technicianId: string,
  zoneId: string
): Promise<void> {
  return await del<void>(`/technicians/${technicianId}/work-zones/${zoneId}`)
}

export async function createTechnicianContact(
  technicianId: string,
  data: CreateContactDto
): Promise<TechnicianContact> {
  return await post<TechnicianContact, CreateContactDto>(
    `/technicians/${technicianId}/contacts`,
    data
  )
}

export async function updateTechnicianContact(
  technicianId: string,
  contactId: string,
  data: UpdateContactDto
): Promise<TechnicianContact> {
  return await patch<TechnicianContact, UpdateContactDto>(
    `/technicians/${technicianId}/contacts/${contactId}`,
    data
  )
}

export async function deleteTechnicianContact(
  technicianId: string,
  contactId: string
): Promise<void> {
  return await del<void>(`/technicians/${technicianId}/contacts/${contactId}`)
}

export async function deleteTechnician(id: string): Promise<void> {
  return await del<void>(`/technicians/${id}`)
}

export async function bulkDeleteTechnicians(
  ids: string[]
): Promise<{ deleted: number; total: number }> {
  return await post<{ deleted: number; total: number }, { ids: string[] }>(
    '/technicians/bulk-delete',
    { ids }
  )
}

export async function addAvailability(
  technicianId: string,
  data: CreateAvailabilityDto
): Promise<TechnicianAvailability> {
  return await post<TechnicianAvailability, CreateAvailabilityDto>(
    `/technicians/${technicianId}/availability`,
    data
  )
}

export async function updateAvailability(
  technicianId: string,
  availabilityId: string,
  data: UpdateAvailabilityDto
): Promise<TechnicianAvailability> {
  return await patch<TechnicianAvailability, UpdateAvailabilityDto>(
    `/technicians/${technicianId}/availability/${availabilityId}`,
    data
  )
}

export async function deleteAvailability(
  technicianId: string,
  availabilityId: string
): Promise<void> {
  return await del<void>(
    `/technicians/${technicianId}/availability/${availabilityId}`
  )
}

export async function getTechnicianFilterCounts(): Promise<TechnicianFilterCounts> {
  const response = await connector.get('/technicians/filter-counts')
  return response.data
}

export async function getTechnicians(
  params: ListTechniciansParams = {}
): Promise<PaginatedTechniciansApiResponse> {
  const queryParams = new URLSearchParams()

  if (params.page) queryParams.set('page', params.page.toString())
  if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString())
  if (params.search) queryParams.set('search', params.search)
  if (params.jobType?.length)
    queryParams.set('jobType', params.jobType.join(','))
  if (params.postalCode) queryParams.set('postalCode', params.postalCode)
  if (params.postalCode && params.radiusKm)
    queryParams.set('radiusKm', params.radiusKm.toString())
  if (params.department) queryParams.set('department', params.department)
  if (params.flags?.length) queryParams.set('flags', params.flags.join(','))

  const queryString = queryParams.toString()
  const url = `/technicians${queryString ? `?${queryString}` : ''}`

  const response = await connector.get(url)
  return response.data
}

export async function getTechnicianDocuments(
  technicianId: string
): Promise<TechnicianDocument[]> {
  return await get<TechnicianDocument[]>(
    `/technicians/${technicianId}/documents`
  )
}

export async function getTechnicianDocumentsCount(technicianId: string) {
  return await getDocumentsCount(technicianId, EntityType.TECHNICIAN)
}

export async function uploadTechnicianDocument(
  technicianId: string,
  file: File,
  documentType?: TechnicianDocumentType
) {
  const formData = new FormData()
  formData.append('documents', file)
  if (documentType) {
    formData.append('documentTypes', JSON.stringify([documentType]))
  }

  const response = await connector.post(
    `/technicians/${technicianId}/documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return response.data
}

export async function deleteTechnicianDocument(
  technicianId: string,
  documentId: string
) {
  return await del(`/technicians/${technicianId}/documents/${documentId}`)
}

/** Report item — one intervention with a reported dispute */
export interface TechnicianReportItem {
  id: string
  /** Per-org sequential reference (e.g. INT-00042); null on legacy rows */
  reference: string | null
  title: string
  externalId: string | null
  siteName: string | null
  siteCity: string | null
  disputeCategory: string | null
  disputeReason: string | null
  hasDispute: boolean
  disputeReportedAt: string | null
  disputeResolvedAt: string | null
}

export interface TechnicianReportsResponse {
  data: TechnicianReportItem[]
  total: number
}

export interface TechnicianReportsParams {
  status?: DisputeStatusFilter
  search?: string
}

export async function getTechnicianReports(
  technicianId: string,
  params?: TechnicianReportsParams
): Promise<TechnicianReportsResponse> {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.search) searchParams.set('search', params.search)
  const qs = searchParams.toString()
  const url = `/technicians/${technicianId}/reports${qs ? `?${qs}` : ''}`
  return await get<TechnicianReportsResponse>(url)
}

export async function getTechnicianReportsCount(
  technicianId: string
): Promise<{ count: number }> {
  return await get<{ count: number }>(
    `/technicians/${technicianId}/reports/count`
  )
}
